/**
 * Command Center — "All tests" for one menu (+ ML or no ML).
 * Tests come from the backend.
 */

import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge, CcStyles, DeleteModal, ErrorBanner, Segments, errMsg } from "./components";
import { CcMode, CcTest, ccApi, ccBase } from "./api";
import { CardInfo, CheckSummary, migrateFlow } from "./common";
import { ML_DEFAULT, MlState, mlCard } from "./mlModel";
import { DECK_DEFAULT, DeckState, deckCard } from "./deckModel";

type Bucket = CardInfo["bucket"];

const COPY: Record<CcMode, { sub: string; summary: { label: string; match: (s: any, c: CardInfo) => boolean; note: (n: number, items: any[]) => string; warn?: boolean }[] }> = {
  ml: {
    sub: "Plan, run and prove geo-lift tests with Adrsta’s models built in. Synthetic control and RCT tests, each through 5 stages: Plan, Design, Test plan, In flight (measured mid-flight), Decide.",
    summary: [
      { label: "Planning / design", match: (s: MlState, c) => !s.saved && s.maxStage <= 2, note: (_n, items) => { const b = items.filter(([, c]: [any, CardInfo]) => c.tone === "red").length; return b ? `${b} has a blocker` : "on track"; }, warn: true },
      { label: "Plan ready", match: (s: MlState) => !s.saved && s.maxStage === 3, note: () => "awaiting approval" },
      { label: "In flight", match: (s: MlState) => !s.saved && s.maxStage === 4 && s.phase < 2, note: () => "read mid-flight" },
      { label: "Ready to decide", match: (s: MlState) => !s.saved && (s.maxStage >= 5 || (s.maxStage === 4 && s.phase >= 2)), note: () => "flight ended", warn: true },
      { label: "Closed", match: (s: MlState) => s.saved, note: () => "in library" },
    ],
  },
  deck: {
    sub: "Run geo tests measured by a partner such as Adrsta. Upload the partner’s design deck and its mid-flight results decks; the Command Center reads them, tracks each test and stores the results. Same 5 stages: Plan, Design, Test plan, In flight, Decide.",
    summary: [
      { label: "Planning / design", match: (s: DeckState) => !s.saved && s.maxStage <= 2, note: (_n, items) => { const b = items.filter(([, c]: [any, CardInfo]) => c.tone === "red").length; return b ? `${b} has a blocker` : "on track"; }, warn: true },
      { label: "Plan ready", match: (s: DeckState) => !s.saved && s.maxStage === 3, note: () => "awaiting approval" },
      { label: "In flight", match: (s: DeckState) => !s.saved && s.maxStage === 4 && s.phase < 2, note: () => "partner reads mid-flight" },
      { label: "Ready to decide", match: (s: DeckState) => !s.saved && (s.maxStage >= 5 || (s.maxStage === 4 && s.phase >= 2)), note: () => "flight ended", warn: true },
      { label: "Closed", match: (s: DeckState) => s.saved, note: () => "in library" },
    ],
  },
};

const TABS: { key: Bucket; label: string }[] = [
  { key: "action", label: "Needs action" },
  { key: "active", label: "Active" },
  { key: "closed", label: "Closed" },
];

export default function CommandCenterTests({ mode }: { mode: CcMode }) {
  const navigate = useNavigate();
  const base = ccBase(mode);
  const [tests, setTests] = useState<CcTest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Bucket>("action");
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setTests(await ccApi.listTests(mode));
    } catch (err) {
      setError(`Failed to load tests: ${errMsg(err)}`);
    } finally {
      setLoading(false);
    }
  }, [mode]);

  useEffect(() => { setTab("action"); load(); }, [load]);

  // Refresh quietly while a design or readout job is running, so its card moves on when it finishes.
  const anyRunning = tests.some(t => t.live && Object.keys(t.live).length > 0);
  useEffect(() => {
    if (!anyRunning) return;
    const timer = setInterval(() => { ccApi.listTests(mode).then(setTests).catch(() => {}); }, 8000);
    return () => clearInterval(timer);
  }, [anyRunning, mode]);

  const items: [any, CardInfo, CcTest][] = tests.map(t => {
    const st = mode === "ml"
      ? migrateFlow(t.state, { ...ML_DEFAULT, ...(t.state as Partial<MlState>), name: t.name, adv: t.advertiser })
      : migrateFlow(t.state, { ...DECK_DEFAULT, ...(t.state as Partial<DeckState>), name: t.name, adv: t.advertiser });
    const checks = (t as CcTest & { check_summary?: CheckSummary }).check_summary;
    const card = mode === "ml" ? mlCard(st as MlState, t.engine || {}, checks, t.live) : deckCard(st as DeckState, t.engine || {}, checks);
    return [st, card, t];
  });
  const counts = { action: 0, active: 0, closed: 0 } as Record<Bucket, number>;
  items.forEach(([, c]) => { counts[c.bucket] += 1; });
  const shown = items.filter(([, c]) => c.bucket === tab);

  const onDelete = async (id: string) => {
    setDeleting(true);
    try {
      await ccApi.deleteTest(id);
      setTests(prev => prev.filter(t => t.id !== id));
    } catch (err) {
      setError(`Delete failed: ${errMsg(err)}`);
    } finally {
      setDeleting(false);
      setConfirmId(null);
    }
  };
  const toDelete = tests.find(t => t.id === confirmId) || null;

  return (
    <div className="cc-page">
      <CcStyles />
      <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "flex-start", gap: 16 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <h1 className="cc-h1">Command Center</h1>
            <Badge tone={mode === "ml" ? "blue" : "violet"}>{mode === "ml" ? "+ ML" : "No ML"}</Badge>
          </div>
          <p className="cc-sub">{COPY[mode].sub}</p>
        </div>
        <button className="cc-btn primary" onClick={() => navigate(`${base}/new`)}>+ New geo test</button>
      </div>

      <ErrorBanner msg={error} onClose={() => setError(null)} />

      <div className="cc-grid-5">
        {COPY[mode].summary.map(sum => {
          const matched = items.filter(([s, c]) => sum.match(s, c));
          const note = sum.note(matched.length, matched);
          const warn = sum.warn && matched.length > 0 && note !== "on track";
          return (
            <div key={sum.label} className="cc-card" style={{ padding: "14px 16px", gap: 2 }}>
              <div className="cc-lbl">{sum.label}</div>
              <div className="cc-val">{loading ? "–" : matched.length}</div>
              <div style={{ fontSize: 11.5, color: warn ? "var(--warning)" : "var(--text-tertiary)" }}>{note}</div>
            </div>
          );
        })}
      </div>

      <div className="cc-tabs" role="tablist" aria-label="Filter">
        {TABS.map(tb => (
          <button key={tb.key} role="tab" aria-selected={tab === tb.key} className={`cc-tab${tab === tb.key ? " on" : ""}`} onClick={() => setTab(tb.key)}>
            {tb.label} · {counts[tb.key]}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="cc-grid-2">{[1, 2, 3, 4].map(i => <div key={i} className="cc-skel" style={{ height: 150 }} />)}</div>
      ) : shown.length === 0 ? (
        <div style={{ textAlign: "center", padding: "56px 24px", color: "var(--text-secondary)", fontSize: 14 }}>
          No tests here yet.{" "}
          <button className="cc-btn" style={{ marginLeft: 8 }} onClick={() => navigate(`${base}/new`)}>+ New geo test</button>
        </div>
      ) : (
        <div className="cc-grid-2">
          {shown.map(([, c, t]) => (
            <div key={t.id} className="cc-tcard" role="button" tabIndex={0}
              onClick={() => navigate(`${base}/tests/${t.id}`)}
              onKeyDown={e => { if (e.key === "Enter") navigate(`${base}/tests/${t.id}`); }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
                <span style={{ fontWeight: 600 }}>{t.advertiser} · {t.name}</span>
                <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <Badge tone={c.tone}>{c.badge}</Badge>
                  <button className="cc-icon-btn" title="Delete test" aria-label="Delete test" onClick={e => { e.stopPropagation(); setConfirmId(t.id); }}>✕</button>
                </span>
              </div>
              {c.result ? (
                <>
                  <div style={{ display: "flex", gap: 20, borderTop: "1px solid var(--border-subtle)", paddingTop: 10 }}>
                    <div><div className="cc-lbl">Lift</div><div className="cc-val" style={{ color: "var(--success-card)" }}>{c.result.lift}</div></div>
                    <div><div className="cc-lbl">{c.result.effLabel}</div><div className="cc-val">{c.result.eff}</div></div>
                    <div><div className="cc-lbl">Bar</div><div className="cc-val" style={{ color: "var(--text-secondary)" }}>{c.result.bar}</div></div>
                  </div>
                  <div style={{ fontSize: 12.5, color: "var(--text-secondary)" }}>Decision recorded: {c.result.decision}</div>
                </>
              ) : (
                <>
                  <Segments done={c.segDone} cur={c.segCur} />
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>{c.chips.map(ch => <span key={ch} className="cc-chip">{ch}</span>)}</div>
                  <div style={{ fontSize: 12.5, color: c.nextWarn ? "var(--warning)" : "var(--text-secondary)", borderTop: "1px solid var(--border-subtle)", paddingTop: 10 }}>{c.next}</div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
      {toDelete && (
        <DeleteModal name={`${toDelete.advertiser} · ${toDelete.name}`} loading={deleting}
          body="The test, its plan and its results will be removed from Command Center. This cannot be undone."
          onConfirm={() => onDelete(toDelete.id)} onCancel={() => setConfirmId(null)} />
      )}
    </div>
  );
}
